import DemoNextSteps from '@/components/DemoNextSteps';
import { useState, useRef, useEffect } from 'react';
import { motion } from 'motion/react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from 'lucide-react';
import PhoneAlertDialog from './PhoneAlertDialog';
import { useContactForm } from '@/hooks/use-contact-form';
import Turnstile from './Turnstile';
import { trackEvent } from '@/lib/analytics';
import SectionEyebrow from '@/components/SectionEyebrow';
import { FadeIn } from '@/components/motion-primitives';

const EASE_OUT = [0.16, 1, 0.3, 1];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    description: '',
  });
  const [contactError, setContactError] = useState(null);
  const [isPhoneDialogOpen, setIsPhoneDialogOpen] = useState(false);
  const [captchaError, setCaptchaError] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [captchaCycle, setCaptchaCycle] = useState(0);
  const { submitContactForm, loading, error, success } = useContactForm();
  const errorRef = useRef(null);
  const formRef = useRef(null);
  const startedRef = useRef(false);
  const viewedRef = useRef(false);
  const trackStep = (step, data = {}) => trackEvent('lead-form-' + step, { form: 'contact', ...data });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !viewedRef.current) {
          viewedRef.current = true;
          trackEvent('lead-form-view', { form: 'contact' });
        }
      },
      { threshold: 0.1 }
    );
    if (formRef.current) observer.observe(formRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollToError = () => {
    if (errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    trackStep('attempt');
    setCaptchaError(null);
    setContactError(null);

    if (!formData.phone.trim()) {
      trackStep('error', { reason: 'contact_missing' });
      setContactError('Lütfen size ulaşabileceğimiz telefon numaranızı girin.');
      setTimeout(scrollToError, 100);
      return;
    }

    if (!turnstileToken) {
      trackStep('error', { reason: 'captcha_missing' });
      setCaptchaError('Doğrulama tamamlanamadı. Lütfen sayfayı yenileyip tekrar deneyiniz.');
      setTimeout(scrollToError, 100);
      return;
    }

    try {
      const result = await submitContactForm({
        name: formData.name,
        phone: formData.phone,
        message: formData.description,
        recaptchaToken: turnstileToken,
      });
      setTurnstileToken(null);
      setCaptchaCycle((cycle) => cycle + 1);

      setTimeout(scrollToError, 100);

      if (result.success) {
        trackEvent('contact-form-submit');
        trackStep('success');
        startedRef.current = false;
        setFormData({ name: '', phone: '', description: '' });
      } else {
        trackStep('error', { reason: 'request_failed' });
      }
    } catch {
      trackStep('error', { reason: 'unexpected' });
      setCaptchaError('Bir hata oluştu. Lütfen daha sonra tekrar deneyiniz.');
      setTimeout(scrollToError, 100);
    }
  };

  const handleChange = (e) => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackStep('start');
    }
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Telefon',
      content: '+90 542 254 55 35',
      description: 'Mevcut müşterilerimize 7/24 destek',
    },
    {
      icon: Mail,
      title: 'E-posta',
      content: 'iletisim@tilkiotomasyon.com',
      description: 'Sorularınız için bize yazın',
    },
    {
      icon: MapPin,
      title: 'Adres',
      content: 'İstanbul, Türkiye',
      description: 'Merkez ofisimiz',
    },
    {
      icon: Clock,
      title: 'Demo ve abonelik başvuruları',
      content: 'Pazartesi - Cuma',
      description: '09:00–18:00 · Başvurulara dönüş ve işlem saatleri',
    },
  ];

  return (
    <section id="contact" className="relative section-y scroll-mt-20 bg-surface-2 overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-50" aria-hidden="true" />

      <div className="container relative mx-auto px-4">
        <FadeIn className="text-center mb-8 sm:mb-10">
          <SectionEyebrow className="mb-4">İletişim</SectionEyebrow>
          <h2 className="type-h2 text-balance mb-4">
            Tilki işletmenize <span className="text-gradient">uygun mu?</span>
          </h2>
          <p className="type-body-lg text-pretty max-w-2xl mx-auto">
            İletişim bilginizi bırakın; masa, sipariş ve adisyon ekranlarını birlikte inceleyelim, sorularınızı
            yanıtlayalım.
          </p>
        </FadeIn>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '0px 0px -80px 0px' }}
            transition={{ duration: 0.7, ease: EASE_OUT }}
          >
            <Card className="shadow-card border border-hairline">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-ink flex items-center font-poppins">
                  Ücretsiz tanıtım isteyin
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DemoNextSteps />
                <form
                  id="demo-contact-form"
                  ref={formRef}
                  onSubmit={handleSubmit}
                  onInvalidCapture={() => trackStep('error', { reason: 'validation' })}
                  className="space-y-5 scroll-mt-24"
                >
                  <p className="text-sm leading-relaxed text-ink-muted">
                    Adınızı ve telefonunuzu bırakın. Sizi arayıp işletmenize uygun tanıtım için birlikte zaman
                    belirleyelim.
                  </p>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-ink mb-2 font-inter">
                        Ad Soyad <span aria-hidden="true">*</span>
                        <span className="sr-only">(zorunlu)</span>
                      </label>
                      <Input
                        id="contact-name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        autoComplete="name"
                        className="w-full font-inter focus-ring-modern"
                        placeholder="Adınızı girin"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-phone" className="block text-sm font-medium text-ink mb-2 font-inter">
                      Telefon <span aria-hidden="true">*</span>
                      <span className="sr-only">(zorunlu)</span>
                    </label>
                    <Input
                      id="contact-phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      autoComplete="tel"
                      aria-describedby="contact-channel-hint"
                      className="w-full font-inter focus-ring-modern"
                      placeholder="Telefon numaranızı girin"
                    />
                    <p id="contact-channel-hint" className="text-xs text-ink-muted mt-1 font-inter">
                      Tanıtım için bu numaradan sizinle iletişime geçeceğiz.
                    </p>
                  </div>

                  <details className="rounded-lg border border-hairline p-4">
                    <summary className="cursor-pointer text-sm font-medium text-ink">Mesaj ekle (isteğe bağlı)</summary>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label
                          htmlFor="contact-description"
                          className="block text-sm font-medium text-ink mb-2 font-inter"
                        >
                          Açıklama
                        </label>
                        <Textarea
                          id="contact-description"
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          rows={4}
                          className="w-full font-inter focus-ring-modern resize-y"
                          placeholder="Mesajınızı buraya yazın (opsiyonel)"
                        />
                      </div>
                    </div>
                  </details>

                  <div className="w-full">
                    <Turnstile
                      key={captchaCycle}
                      onVerify={(token) => {
                        setTurnstileToken(token);
                        setCaptchaError(null);
                      }}
                      onError={() => {
                        setTurnstileToken(null);
                        setCaptchaError('Doğrulama yüklenemedi. Lütfen sayfayı yenileyiniz.');
                        trackStep('error', { reason: 'captcha_load' });
                      }}
                      onExpire={() => setTurnstileToken(null)}
                    />
                  </div>

                  <div ref={errorRef} aria-live="polite" aria-atomic="true">
                    {contactError && (
                      <div
                        role="alert"
                        className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm font-inter"
                      >
                        {contactError}
                      </div>
                    )}

                    {error && (
                      <div
                        role="alert"
                        className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm font-inter"
                      >
                        {error}
                      </div>
                    )}

                    {captchaError && (
                      <div
                        role="alert"
                        className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm font-inter"
                      >
                        {captchaError}
                      </div>
                    )}

                    {success && (
                      <div
                        role="status"
                        className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm font-inter"
                      >
                        Tanıtım talebiniz alındı. Bıraktığınız telefon numarasından size ulaşıp tanıtım zamanını
                        birlikte belirleyeceğiz.
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="w-full modern-btn-large disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={loading}
                    aria-busy={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" aria-hidden="true" />
                        Gönderiliyor...
                      </>
                    ) : (
                      <>
                        Tanıtım talebimi gönder
                        <Send className="w-5 h-5 ml-2" aria-hidden="true" />
                      </>
                    )}
                  </button>
                  <p className="text-center text-xs leading-relaxed text-ink-muted">
                    Bu form bir tanıtım talebidir; abonelik veya ödeme başlatmaz.
                  </p>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0, x: 32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '0px 0px -80px 0px' }}
            transition={{ duration: 0.7, ease: EASE_OUT, delay: 0.1 }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {contactInfo.map((info, index) => (
                <Card
                  key={index}
                  className={`bg-surface border border-hairline shadow-card hover:border-brand-200 hover:shadow-card-lift transition-all duration-300 ${
                    index < 2 ? 'sm:col-span-2' : ''
                  }`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start space-x-3">
                      <div className="bg-brand-50 text-brand-700 p-2.5 rounded-xl">
                        <info.icon className="w-5 h-5" aria-hidden="true" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-xs font-semibold text-ink-muted uppercase tracking-[0.1em] mb-1 font-inter">
                          {info.title}
                        </h3>
                        <p className="text-base text-ink font-medium font-inter break-words">{info.content}</p>
                        <p className="text-xs text-ink-muted mt-1 font-inter">{info.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Quick Actions */}
            <Card className="bg-ink-strong text-white border-0 overflow-hidden relative">
              <div className="brand-glow w-72 h-72 -top-20 -right-20 opacity-40" aria-hidden="true" />
              <CardContent className="p-6 relative">
                <h3 className="text-xl font-bold text-white mb-4 font-poppins tracking-tight">Hızlı İşlemler</h3>
                <div className="space-y-3">
                  <button
                    type="button"
                    className="w-full modern-btn-on-dark"
                    onClick={() => {
                      if (/Mobi|Android/i.test(navigator.userAgent)) {
                        window.location.href = 'tel:+905422545535';
                      } else {
                        setIsPhoneDialogOpen(true);
                      }
                    }}
                  >
                    <Phone className="w-4 h-4 mr-2" aria-hidden="true" />
                    Hemen Ara: +90 542 254 55 35
                  </button>
                  <button
                    type="button"
                    className="w-full modern-btn-on-dark"
                    onClick={() => {
                      window.open(
                        'https://mail.google.com/mail/?view=cm&fs=1&to=iletisim@tilkiotomasyon.com&su=Tilki%20Otomasyon%20Hakkında&body=Merhaba%20Tilki%20Otomasyon%20Ekibi,',
                        '_blank',
                        'noopener,noreferrer'
                      );
                    }}
                  >
                    <Mail className="w-4 h-4 mr-2" aria-hidden="true" />
                    E-posta Gönder
                  </button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>

      {/* Phone Alert Dialog */}
      <PhoneAlertDialog
        isOpen={isPhoneDialogOpen}
        onClose={() => setIsPhoneDialogOpen(false)}
        phoneNumber="+905422545535"
      />
    </section>
  );
};

export default Contact;
